import { useOrganization } from "@/contexts/OrganizationContext";
import { useTeamsWithCounts } from "@/hooks/useTeams";
import { useAuth } from "@/contexts/AuthContext";
import { useMemo } from "react";

export type OrgAccessLevel = "owner" | "admin" | "manager" | "member";
export type OrgMemberRole = "owner" | "admin" | "manager" | "member";

export interface PermissionsResult {
  // Current role
  role: OrgAccessLevel;
  
  // Effective access level (considers team leadership)
  effectiveLevel: OrgAccessLevel;
  
  // Boolean helpers
  isOwner: boolean;
  isAdmin: boolean;
  isManager: boolean; // True for managers OR team leaders
  isMember: boolean;
  
  // Permission checks
  canManageSettings: boolean;      // Account settings, billing
  canManageUsers: boolean;         // Invite/remove users
  canManageTeams: boolean;         // Create/edit teams
  canManageOwnTeam: boolean;       // Edit own team (for managers)
  canCreatePipelines: boolean;     // Create/edit pipelines
  canManageAutomations: boolean;   // Create/edit automations
  canViewReports: boolean;         // View advanced reports
  canManageScripts: boolean;       // Create/edit scripts
  canViewAllOpportunities: boolean; // See all opportunities
  canViewTeamOpportunities: boolean; // See team opportunities
  canCreateOpportunities: boolean;  // Create opportunities
  canExecuteScripts: boolean;       // Run scripts
  canInviteUsers: boolean;          // Send invitations
  canDeleteRecords: boolean;        // Delete contacts, companies, opportunities
  
  // Team-related info
  isTeamLeader: boolean;
  ledTeamIds: string[];
  memberTeamIds: string[];
  
  // Allowed org roles for invitation
  allowedOrgRoles: OrgMemberRole[];
}

export function usePermissions(): PermissionsResult {
  const { currentMembership, organization, isProviderMember } = useOrganization();
  const { userRoles } = useAuth();
  const { data: teams } = useTeamsWithCounts(organization?.id);
  
  const role: OrgAccessLevel = (currentMembership?.role as OrgAccessLevel) || "member";
  
  // Check if user is a platform admin (NeoSDR staff with admin/editor/suporte role)
  const isPlatformAdmin = useMemo(() => 
    userRoles.includes('admin') || userRoles.includes('editor') || userRoles.includes('suporte'),
    [userRoles]
  );
  
  // Calculate team leadership status
  const { isTeamLeader, ledTeamIds, memberTeamIds } = useMemo(() => {
    if (!teams || !currentMembership) {
      return { isTeamLeader: false, ledTeamIds: [] as string[], memberTeamIds: [] as string[] };
    }
    
    const ledIds: string[] = [];
    const memberIds: string[] = [];
    
    teams.forEach(team => {
      const membership = team.members?.find(m => m.user_id === currentMembership.user_id);
      if (membership) {
        memberIds.push(team.id);
        if (membership.role === 'leader') {
          ledIds.push(team.id);
        }
      }
    });
    
    return {
      isTeamLeader: ledIds.length > 0,
      ledTeamIds: ledIds,
      memberTeamIds: memberIds,
    };
  }, [teams, currentMembership]);
  
  // Determine effective access level
  // Platform admins and team leaders get elevated access
  const effectiveLevel: OrgAccessLevel = useMemo(() => {
    // Platform admins (NeoSDR staff) get owner-level access
    if (isPlatformAdmin) return 'owner';
    if (role === 'owner' || role === 'admin') return role;
    if (role === 'manager' || isTeamLeader) return 'manager';
    return 'member';
  }, [role, isTeamLeader, isPlatformAdmin]);
  
  // Provider members (NeoSDR staff) or platform admins get full access
  const hasFullAccess = isPlatformAdmin || isProviderMember || effectiveLevel === 'owner' || effectiveLevel === 'admin';
  const hasManagerAccess = hasFullAccess || effectiveLevel === 'manager';
  
  // Calculate allowed org roles for invitations
  const allowedOrgRoles: OrgMemberRole[] = useMemo(() => {
    if (hasFullAccess) {
      // Owner/Admin can invite admin, manager, or member
      return ['admin', 'manager', 'member'];
    }
    if (hasManagerAccess) {
      // Manager can only invite member
      return ['member'];
    }
    return [];
  }, [hasFullAccess, hasManagerAccess]);
  
  return {
    // Role info
    role,
    effectiveLevel,
    
    // Level checks
    isOwner: effectiveLevel === 'owner',
    isAdmin: effectiveLevel === 'admin' || effectiveLevel === 'owner',
    isManager: hasManagerAccess,
    isMember: true, // Everyone is at least a member
    
    // Permissions
    canManageSettings: hasFullAccess,
    canManageUsers: hasFullAccess,
    canManageTeams: hasFullAccess,
    canManageOwnTeam: hasManagerAccess,
    canCreatePipelines: hasFullAccess,
    canManageAutomations: hasFullAccess,
    canViewReports: hasManagerAccess,
    canManageScripts: hasManagerAccess,
    canViewAllOpportunities: hasFullAccess,
    canViewTeamOpportunities: hasManagerAccess,
    canCreateOpportunities: true, // All members can create
    canExecuteScripts: true, // All members can execute
    canInviteUsers: hasFullAccess, // Only owner/admin can invite
    canDeleteRecords: hasManagerAccess, // Owner, admin, manager/leader can delete
    
    // Team info
    isTeamLeader,
    ledTeamIds,
    memberTeamIds,
    
    // Invitation roles
    allowedOrgRoles,
  };
}
